import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForegroundInterval } from '../../services/AppStateService';
import { formatCompletionWallTime, parseCompletionWallTime } from './workoutCompletionTime';

export function canEditWorkoutCompletionTime(workout, now = new Date()) {
  if (!workout || workout.type !== 'workout') return false;
  if (!['complete', 'completed'].includes(workout.status) || !workout.completed_at) return false;
  if (workout.is_template) return false;
  if (workout.travel_replacement_locked === true || Boolean(workout.travel_replacement_id)) return false;
  if (workout.travel_replacement_for_activity_id) return false;
  return !workout.due_at || new Date(workout.due_at) >= now;
}

export default function WorkoutCompletionTimeEditor({
  workout,
  backendService,
  onUpdated,
  readOnly = false,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
}) {
  const [now, setNow] = useState(() => new Date());
  const initialValue = useMemo(
    () => formatCompletionWallTime(workout?.completed_at, timezone),
    [timezone, workout?.completed_at],
  );
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const lastSavedRef = useRef(null);

  useEffect(() => {
    setValue(initialValue);
    const preservedSave = lastSavedRef.current;
    if (preservedSave?.workoutId !== String(workout?._id)
        || preservedSave?.value !== initialValue) {
      setMessage('');
    }
    setError('');
  }, [initialValue, workout?._id]);

  useForegroundInterval(() => setNow(new Date()), workout?.due_at ? 1000 : null);

  if (readOnly || !canEditWorkoutCompletionTime(workout, now)) return null;

  const save = async () => {
    const parsed = parseCompletionWallTime(value, timezone);
    if (!parsed) {
      setError('Choose a valid completion date and time.');
      return;
    }
    if (parsed > new Date()) {
      setError('Completion time cannot be in the future.');
      return;
    }

    setSaving(true);
    setMessage('');
    setError('');
    try {
      const updated = await backendService.updateWorkoutCompletionTime(workout._id, parsed.toISOString());
      lastSavedRef.current = { workoutId: String(workout._id), value };
      onUpdated?.(updated, {
        focusActivityId: String(updated?._id || workout._id),
        focusCompletedAt: updated?.completed_at,
        refreshDojoAgenda: true,
      });
      setMessage('Completion time updated');
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Could not update completion time.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="workout-completion-time-editor">
      <label htmlFor={`workout-completed-at-${workout._id}`}>Completed</label>
      <div className="workout-completion-time-controls">
        <input
          id={`workout-completed-at-${workout._id}`}
          type="datetime-local"
          value={value}
          max={formatCompletionWallTime(now, timezone)}
          onChange={event => {
            setValue(event.target.value);
            setMessage('');
            setError('');
          }}
        />
        <button
          type="button"
          className="btn-select workout-completion-time-save"
          disabled={saving || !value || value === initialValue}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {message && <p className="workout-completion-time-message" role="status">{message}</p>}
      {error && <p className="workout-completion-time-error" role="alert">{error}</p>}
    </div>
  );
}
